import {
  Box,
  Center,
  Flex,
  Grid,
  Paper,
  Text,
  Title,
  Tooltip,
  useMantineTheme,
  rem,
  Group,
  ActionIcon,
} from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import {
  EditIcon,
  EmailIcon,
  FacebookIcon,
  LinkedInIcon,
  LocationIcon,
  PhoneIcon,
  TwitterIcon,
} from "../../shared/svg";
import { ProgressAvatar } from "../../shared/components/ProgressAvatar";
import "./Profile.scss";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import useProfileStore from "../../store/useProfileStore";
import useAuthStore from "../../store/useAuthStore";
import { GithubIcon } from "../../shared/svg/GithubIcon";
import { useEffect, useRef, useState } from "react";
import { ClearIconWhite } from "../../shared/svg/ClearIcon";
import { OnboardingTour } from "@gfazioli/mantine-onboarding-tour";

export const ContactInfo = () => {
  const laptop = useMediaQuery("(max-width: 1100px)");
  const mobile = useMediaQuery("(max-width: 650px)");
  const { t } = useTranslation();
  const theme = useMantineTheme();

  const { user } = useAuthStore();
  const { profile, editTooltipShow } = useProfileStore();
  const [tooltipOpened, setTooltipOpened] = useState(false);

  const contacts = profile?.contact_details;

  useEffect(() => {
    if (profile?.profile_completeness?.completeness !== 100) {
      const timeout = setTimeout(() => setTooltipOpened(true), 500);
      return () => clearTimeout(timeout);
    }
  }, [profile?.profile_completeness?.completeness]);

  const handleDontShow = async () => {
    try {
      await editTooltipShow(true);
      setTooltipOpened(false);
    } catch (err) {
      console.log(err);
    }
  };

  const targetRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setTooltipOpened(true); // елемент знову в полі зору
        } else {
          setTooltipOpened(false); // елемент зник з поля зору
        }
      },
      { threshold: 0.1 }
    );

    const el = targetRef.current;
    if (el) {
      observer.observe(el);
    }

    return () => {
      if (el) {
        observer.unobserve(el);
      }
    };
  }, []);

  const fieldLabels: Record<string, string> = {
    avatar: t("avatar"),
    certificate: t("certificate"),
    cv: t("cv"),
    email: t("email"),
    education: t("education"),
    experience: t("experience"),
    facebook: t("facebook"),
    language: t("language"),
    linkedin: t("linkedin"),
    location: t("location"),
    phone: t("phone"),
    skills: t("skills"),
    survey: t("profileEdit.goals"),
    twitter: t("twitter"),
  };

  const missingFields = profile?.profile_completeness.filled_fields
    ? Object.entries(profile?.profile_completeness.filled_fields)
        .filter(([_, value]) => value === false)
        .map(([key]) => fieldLabels[key] || key)
    : [];

  const additionalHint =
    profile?.profile_completeness.completeness !== 100 &&
    missingFields.length > 0
      ? `${missingFields.join(", ")}`
      : "";

  const url = process.env.REACT_APP_BACKEND_URL;
  const cleanedUrl = url?.endsWith("/") ? url.slice(0, -1) : url;

  const avatarFile = profile?.avatar?.file;

  const avatarUrl = avatarFile?.startsWith("http")
    ? avatarFile
    : `${cleanedUrl}${avatarFile ?? ""}`;

  const username =
    profile?.user?.first_name || profile?.user?.last_name
      ? `${profile?.user?.first_name || ""} ${
          profile?.user?.last_name || ""
        }`.trim()
      : user?.full_name || " ";

  const userProfile = (
    <Flex align="end" justify="center" direction="column">
      <ProgressAvatar
        imageUrl={avatarUrl}
        progress={profile?.profile_completeness?.completeness || 0}
      />
      <Text mt={16}>{t("your_profile_strength")}</Text>

      <Tooltip
        label={
          <Group justify="space-between" align="flex-start" gap={0}>
            <Flex justify="space-between" w="100%" pos="relative">
              <div />
              <ActionIcon
                onClick={() => setTooltipOpened(false)}
                aria-label="Close"
                variant="subtle"
                color="white"
                style={{
                  top: mobile ? -22 : -12,
                  right: mobile ? -18 : -10,
                  position: "absolute",
                  pointerEvents: "all",
                }}
              >
                <ClearIconWhite />
              </ActionIcon>
            </Flex>
            <Text
              style={{
                fontSize: mobile ? rem(12) : rem(16),
                maxWidth: rem(460),
                lineHeight: 1.4,
                fontWeight: 600,
              }}
            >
              {t("profile_completeness_title")}
            </Text>
            <Text
              style={{
                fontSize: mobile ? rem(12) : rem(16),
                maxWidth: rem(460),
                lineHeight: 1.4,
              }}
            >
              {t("profile_completeness")}
            </Text>

            <Text
              mt="12px"
              style={{
                fontSize: mobile ? rem(12) : rem(16),
                maxWidth: rem(460),
                lineHeight: 1.4,
              }}
            >
              {t("profile_completeness_add")}
              {additionalHint}
            </Text>
            <Text
              onClick={handleDontShow}
              mt="12px"
              style={{
                fontSize: mobile ? rem(12) : rem(16),
                pointerEvents: "all",
                cursor: "pointer",
                textDecoration: "underline",
                color: theme.colors.violet[1],
                lineHeight: 1.4,
              }}
            >
              {t("not_show_tooltip")}
            </Text>
          </Group>
        }
        withArrow
        zIndex={1}
        opened={
          tooltipOpened &&
          profile?.profile_completeness.completeness !== 100 &&
          profile?.display_preferences?.hide_profile_completeness_tooltip !==
            true &&
          profile?.display_preferences?.hide_tour_tooltip_on_profile === true
        }
        position={mobile ? "bottom" : "left"}
        offset={8}
        arrowSize={12}
        transitionProps={{ duration: 150, transition: "fade" }}
        styles={{
          tooltip: {
            backgroundColor: theme.colors.violet[6],
            color: theme.white,
            fontSize: mobile ? rem(12) : rem(16),
            padding: `${rem(30)} ${rem(22)}`,
            borderRadius: rem(16),
            boxShadow: theme.shadows.lg,
            maxWidth: rem(345),
            lineHeight: 1.4,
            wordWrap: "break-word" as "break-word",
            whiteSpace: "normal",
            zIndex: 1,
          },
          arrow: {
            backgroundColor: theme.colors.violet[6],
          },
        }}
      >
        <Title ref={targetRef} mt={8} size={36} fw={900} ta="center" mr="35px">
          {profile?.profile_completeness?.completeness}%
        </Title>
      </Tooltip>
    </Flex>
  );

  const contactItems = [
    {
      icon: EmailIcon,
      label: t("email"),
      value: contacts?.email || user?.email,
      href: `mailto:${contacts?.email}`,
    },
    {
      icon: PhoneIcon,
      label: t("phone"),
      value: contacts?.phone,
      href: `tel:${contacts?.phone}`,
    },
    {
      icon: LocationIcon,
      label: t("location"),
      value:
        contacts?.country && contacts?.city
          ? `${contacts.country}, ${contacts.city}`
          : contacts?.country
          ? contacts.country
          : contacts?.city
          ? contacts.city
          : "",
    },
    {
      icon: LinkedInIcon,
      label: t("linkedin"),
      value: contacts?.linkedIn,
      href: contacts?.linkedIn,
    },
    {
      icon: TwitterIcon,
      label: t("twitter"),
      value: contacts?.twitter,
      href: contacts?.twitter,
    },
    {
      icon: FacebookIcon,
      label: t("facebook"),
      value: contacts?.facebook,
      href: contacts?.facebook,
    },
    {
      icon: GithubIcon,
      label: t("github"),
      value: contacts?.github,
      href: contacts?.github,
    },
  ];

  const filteredContactItems = contactItems.filter(
    ({ value }) => value && value.trim() !== ""
  );

  const navigate = useNavigate();

  return mobile ? (
    <Paper withBorder radius="24px" p={32} h="100%" pos="relative">
      <div className="edit-icon">
        <OnboardingTour.Target id="profile-edit">
          <ActionIcon
            variant="transparent"
            onClick={() => navigate("/profile/edit/contacts")}
          >
            <EditIcon />
          </ActionIcon>
        </OnboardingTour.Target>
      </div>
      <Grid>
        {laptop && (
          <Center m="auto" mb={40}>
            {userProfile}
          </Center>
        )}
        <OnboardingTour.Target id="profile-info">
          <Grid.Col span={laptop ? 12 : 8}>
            <Title size={36} lh="50px">
              {username}
            </Title>
            <Box mt={40} className="contact-info">
              {filteredContactItems.map(
                ({ icon: Icon, label, value, href }, index) => (
                  <Box key={index} className="contact-item">
                    <Flex gap={16}>
                      <div className="icon">
                        <Icon />
                      </div>
                      <Text>{label}</Text>
                    </Flex>
                    {href ? (
                      <Link target="_blank" rel="noopener noreferrer" to={href}>
                        {value}
                      </Link>
                    ) : (
                      <Text>{value}</Text>
                    )}
                  </Box>
                )
              )}
            </Box>
          </Grid.Col>
        </OnboardingTour.Target>
        <Grid.Col span={4}>{!laptop && userProfile}</Grid.Col>
      </Grid>
    </Paper>
  ) : (
    <OnboardingTour.Target id="profile-info">
      <Paper withBorder radius="24px" p={32} h="100%" pos="relative">
        <div className="edit-icon">
          <OnboardingTour.Target id="profile-edit">
            <ActionIcon
              variant="transparent"
              onClick={() => navigate("/profile/edit/contacts")}
            >
              <EditIcon />
            </ActionIcon>
          </OnboardingTour.Target>
        </div>
        <Grid>
          {laptop && (
            <Center m="auto" mb={40}>
              {userProfile}
            </Center>
          )}
          <Grid.Col span={laptop ? 12 : 8}>
            <Title size={36} lh="50px">
              {username}
            </Title>
            <Box mt={40} className="contact-info">
              {filteredContactItems.map(
                ({ icon: Icon, label, value, href }, index) => (
                  <Box key={index} className="contact-item">
                    <Flex gap={16}>
                      <div className="icon">
                        <Icon />
                      </div>
                      <Text>{label}</Text>
                    </Flex>
                    {href ? (
                      <Link target="_blank" rel="noopener noreferrer" to={href}>
                        {value}
                      </Link>
                    ) : (
                      <Text>{value}</Text>
                    )}
                  </Box>
                )
              )}
            </Box>
          </Grid.Col>
          <Grid.Col span={4}>{!laptop && userProfile}</Grid.Col>
        </Grid>
      </Paper>
    </OnboardingTour.Target>
  );
};
